import {
  Row,
  Col,
  Spin,
  message,
  Drawer,
  Input,
  Button,
  Select,
  Pagination,
  Breadcrumb,
} from "antd";
import { useEffect, useState } from "react";
import ProductCard from "../../components/ProductCard";
import api from "../../services/api";
import ProductDetailContent from "./ProductDetailContent";
import { useNavigate, useLocation } from "react-router-dom";

export default function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    category_id: null,
    subcategory_id: null,
    brand_id: null,
    model_id: null,
    color_id: null,
  });

  const [type, setType] = useState(null);

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [cartItems, setCartItems] = useState([]);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 54;

  const [initialized, setInitialized] = useState(false);

  /* 🔥 URL → FILTERS (Single Source of Truth) */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const newFilters = {
      search: params.get("search") || "",
      category_id: params.get("category_id")
        ? Number(params.get("category_id"))
        : null,
      subcategory_id: params.get("subcategory_id")
        ? Number(params.get("subcategory_id"))
        : null,
      brand_id: params.get("brand_id") ? Number(params.get("brand_id")) : null,
      model_id: params.get("model_id") ? Number(params.get("model_id")) : null,
      color_id: params.get("color_id") ? Number(params.get("color_id")) : null,
    };

    setFilters(newFilters);
    setType(params.get("type") || null);
    setPage(1);

    setInitialized(true);
  }, [location.search]);

  /* 🔥 API CALL */
  useEffect(() => {
    if (!initialized) return;
    loadProducts();
  }, [filters, page, type, initialized]);

  /* 🔥 LOAD PRODUCTS */
  const loadProducts = async () => {
    try {
      setLoading(true);

      const cleanParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null && v !== ""),
      );

      const res = await api.get("/products", {
        params: {
          ...cleanParams,
          page,
          limit: pageSize,
          sortBy: "createdAt",
          sortOrder: "DESC",
          type,
        },
      });

      let data = Array.isArray(res.data) ? res.data : res.data.data;

      setProducts(data);
      setTotal(res.data.total);
    } catch {
      message.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  /* 🔥 LOAD FILTER OPTIONS */
  useEffect(() => {
    if (!initialized) return;

    loadFilters(filters);
  }, [filters, initialized]);

  const loadFilters = async (params = {}) => {
    const res = await api.get("/products/filters", { params });

    const categoriesData = res.data.categories || [];
    const subcategoriesData = res.data.subcategories || [];

    setBrands(res.data.brands || []);
    setModels(res.data.models || []);
    setColors(res.data.colors || []);

    setCategories(categoriesData);
    setSubcategories(subcategoriesData);
  };

  /* 🔥 FILTER MODELS */
  useEffect(() => {
    if (filters.brand_id && models.length) {
      setFilteredModels(
        models.filter((m) => Number(m.brand_id) === Number(filters.brand_id)),
      );
    } else {
      setFilteredModels(models);
    }
  }, [filters.brand_id, models]);

  /* 🔥 CART */
  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const res = await api.get("/cart");
    setCartItems(res.data || []);
  };

  /* 🔥 UI ACTIONS → UPDATE URL (NOT STATE DIRECTLY) */

  const updateURL = (updatedFilters) => {
    const params = new URLSearchParams();

    if (updatedFilters.search) params.set("search", updatedFilters.search);
    if (updatedFilters.category_id)
      params.set("category_id", updatedFilters.category_id);
    if (updatedFilters.subcategory_id)
      params.set("subcategory_id", updatedFilters.subcategory_id);
    if (updatedFilters.brand_id)
      params.set("brand_id", updatedFilters.brand_id);
    if (updatedFilters.model_id)
      params.set("model_id", updatedFilters.model_id);
    if (updatedFilters.color_id)
      params.set("color_id", updatedFilters.color_id);
    if (type) params.set("type", type);

    navigate(`/product-list?${params.toString()}`);
  };

  const openDetail = (product) => {
    setSelectedProduct(product);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  };

  const categoryName = categories?.[0]?.name;
  const subcategoryName = subcategories?.[0]?.name;
  return (
    <>
      {categoryName && (
        <Breadcrumb
          style={{
            marginBottom: 16,
            marginTop: 10,
          }}
          items={[
            {
              title: categoryName,
            },
            ...(subcategoryName
              ? [
                  {
                    title: subcategoryName,
                  },
                ]
              : []),
          ]}
        />
      )}
      {/* 🔥 FILTER BAR */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col>
          <Input.Search
            placeholder="Search product..."
            value={filters.search}
            onChange={(e) => updateURL({ ...filters, search: e.target.value })}
          />
        </Col>

        <Col>
          <Select
            placeholder="Brand"
            style={{ width: 160 }}
            allowClear
            value={filters.brand_id}
            onChange={(val) =>
              updateURL({
                ...filters,
                brand_id: val,
                model_id: null,
              })
            }
            options={brands.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
          />
        </Col>

        <Col>
          <Select
            placeholder="Model"
            style={{ width: 160 }}
            allowClear
            value={filters.model_id}
            disabled={!filters.brand_id}
            onChange={(val) => updateURL({ ...filters, model_id: val })}
            options={filteredModels.map((m) => ({
              value: m.id,
              label: m.name,
            }))}
          />
        </Col>

        <Col>
          <Select
            placeholder="Variants"
            style={{ width: 160 }}
            allowClear
            value={filters.color_id}
            onChange={(val) => updateURL({ ...filters, color_id: val })}
            options={colors.map((c) => ({
              value: c.id,
              label: c.value,
            }))}
          />
        </Col>

        <Col>
          <Button
            onClick={() =>
              updateURL({
                search: "",
                category_id: filters.category_id,
                subcategory_id: filters.subcategory_id,
                brand_id: null,
                model_id: null,
                color_id: null,
              })
            }
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* 🔥 PRODUCTS */}
      {loading ? (
        <div style={{ textAlign: "center", marginTop: 50 }}>
          <Spin size="large" />
        </div>
      ) : products.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            marginTop: 80,
            color: "#999",
          }}
        >
          <h3>No products found</h3>
          <p>Try changing filters or search keyword</p>
        </div>
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
          {products.map((product) => (
            <Col key={product.id} xs={24} sm={12} md={8} lg={4}>
              <ProductCard
                product={product}
                onDetail={() => openDetail(product)}
                cartItems={cartItems}
              />
            </Col>
          ))}
        </Row>
      )}

      {products.length > 0 && total > pageSize && (
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          onChange={(p) => setPage(p)}
          style={{ textAlign: "center", marginTop: 20 }}
        />
      )}

      {/* 🔥 DRAWER */}
      <Drawer
        width={750}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {selectedProduct && (
          <ProductDetailContent
            product={selectedProduct}
            allProducts={products}
            cartItems={cartItems}
          />
        )}
      </Drawer>
    </>
  );
}
